import { formatMoney, money } from '../lib/money';

const N = (minor: number) => formatMoney(money(minor));

/** "Chika Stores'" / "Ade Fabrics Ltd's" — how a business's name takes an apostrophe. */
export function possessive(name: string): string {
  return /s$/i.test(name.trim()) ? `${name.trim()}'` : `${name.trim()}'s`;
}

type Role = 'owner' | 'manager' | 'staff' | 'accountant';
type Method = 'cash' | 'transfer' | 'pos' | 'other';

/** What app fixes are refused for, in words (spec 3A §9; plan decisions). */
const FIX_REFUSED: Record<string, string> = {
  bank_matched: 'This one matches your bank alert, so it stays as the bank says.',
  same_direction: "It's already recorded that way.",
  has_payments: "Payments were already taken off this tab, so this can't change here.",
  settlement: "This is a payment against a tab, so this can't change here.",
  split_payment: "This one was paid in more than one way, so the amount can't change here.",
  removed: 'This entry was removed.',
  not_a_sale: 'Only sales can have money still owing.',
  future_date: "That date hasn't come yet.",
  owed_over_amount: "That's more than the whole amount.",
  unknown_category: 'Pick one of the categories shown.',
  nothing_to_confirm: 'This one is already confirmed.',
};

const REMOVE_REFUSED: Record<string, string> = {
  bank_matched: "This one matches your bank alert, so it can't be removed. Fix it instead.",
  has_payments: "Payments were already taken off this tab, so it can't be removed.",
  settlement: "This is a payment against a tab, so it can't be removed.",
  already_removed: 'This entry was already removed.',
};

const CAPTURE_FAILED: Record<string, string> = {
  upgrade_required: 'the app comes with Business',
  forbidden: "your role can't record here",
  not_found: "you're no longer on these books",
  invalid_input: 'it was empty or too long',
};

const OUR_ERROR = 'Something went wrong on our side. Nothing was lost. Try again.';

/**
 * Every string the web app shows (spec 3A §7, §9). The app speaks to the
 * person as "you"; the agent's notes speak as "I". Sentence case, verb-first
 * buttons of 1–3 words, no emoji (design system: content fundamentals).
 * Copy is design: changes here are reviewed against docs/conversation-ux.md.
 */
export const appCopy = {
  // ── Login (§7.9) ──────────────────────────────────────────────────────────
  wordmark: () => 'Zibanu',
  loginTitle: () => 'Open your books',
  loginLead: () => "We'll send a code to your WhatsApp.",
  loginNumberLabel: () => 'Your WhatsApp number',
  loginSendCode: () => 'Send code',
  loginChatHint: () => 'Already chatting with Zibanu? Send "open app" and tap the link it gives you.',
  loginBadNumber: () => 'Enter your full number, like 0803 111 9999.',
  loginBusy: () => 'Too many tries from here. Wait a few minutes, then try again.',
  codeTitle: () => 'Enter the code',
  codeLead: (ending: string) => `We sent it to the WhatsApp ending ${ending}.`,
  codeLabel: () => 'Code',
  codeOpen: () => 'Open my books',
  codeResend: () => 'Send a new code',
  codeResendIn: (seconds: number) => `New code in ${seconds}s`,
  codeChangeNumber: () => 'Change number',
  codeWrong: (triesLeft: number) =>
    triesLeft === 1 ? "That code isn't right. 1 try left." : `That code isn't right. ${triesLeft} tries left.`,
  codeSpent: () => 'That code has no tries left. Send a new one.',
  codeDormant: () => 'Send a message to Zibanu on WhatsApp first, then try again.',
  codeLocked: () => 'This account is paused. Message us on WhatsApp.',
  linkOpening: () => 'Opening your books…',
  linkBad: () => 'That link has expired or was already used. Send "open app" on WhatsApp for a new one.',

  // ── Shell ─────────────────────────────────────────────────────────────────
  tabHome: () => 'Home',
  tabRecords: () => 'Records',
  tabCapture: () => 'Capture',
  tabCustomers: () => 'Customers',
  tabMore: () => 'More',
  greeting: (hour: number, name: string | null) => {
    const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
    return name ? `Good ${part}, ${name}` : `Good ${part}`;
  },
  searchRecords: () => 'Search records',
  chooseDates: () => 'Choose dates',
  alerts: () => 'Alerts',
  back: () => 'Back',
  close: () => 'Close',
  switchBusiness: () => 'Switch business',
  addBusiness: () => 'Add a business',
  role: (r: Role) => ({ owner: 'Owner', manager: 'Manager', staff: 'Staff', accountant: 'Accountant' })[r],

  // ── Home (§7.1, §7.2) ─────────────────────────────────────────────────────
  salesToday: () => 'Sales today',
  entriesToday: (n: number) => (n === 1 ? '1 entry today' : `${n} entries today`),
  spentToday: () => 'Spent today',
  owedToYou: () => 'Owed to you',
  customerCount: (n: number) => (n === 1 ? '1 customer' : `${n} customers`),
  yourSalesToday: () => 'Your sales today',
  youRecorded: (n: number) => (n === 1 ? 'You recorded 1 entry' : `You recorded ${n} entries`),
  youSpent: () => 'You spent',
  yourCustomersOwe: () => 'Your customers owe',
  fromZibanu: () => 'From Zibanu',
  seeAll: () => 'See all',
  today: () => 'Today',
  allRecords: () => 'All records',
  whatYouRecorded: () => 'What you recorded',
  allYours: () => 'All yours',
  toCheck: (n: number) => (n === 1 ? '1 entry to check' : `${n} entries to check`),
  toCheckLead: () => 'Guessed, or maybe personal.',
  review: () => 'Review',
  nothingToday: () => 'Nothing recorded yet today. Tap + to add a sale or expense.',

  // ── Records (§7.4) ────────────────────────────────────────────────────────
  recordsTitle: () => 'Records',
  toCheckTitle: () => 'To check',
  filterLabel: () => 'Show',
  filterAll: () => 'All',
  filterIn: () => 'Money in',
  filterOut: () => 'Money out',
  searchPlaceholder: () => 'Customer, item or amount',
  fromDate: () => 'From',
  toDate: () => 'To',
  clearDates: () => 'Clear dates',
  datesAndCategory: () => 'Dates and category',
  yesterday: () => 'Yesterday',
  sale: () => 'Sale',
  expense: () => 'Expense',
  badgeOwing: () => 'Owing',
  badgeGuessed: () => 'Guessed',
  badgePersonal: () => 'Personal?',
  badgeRemoved: () => 'Removed',
  you: () => 'You',
  /** Mid-sentence: "Recorded by you", "Removed by you". */
  youObject: () => 'you',
  noRecords: () => 'No records here yet.',
  noMatches: () => 'Nothing matches. Try another word or other dates.',
  showMore: () => 'Show more',

  // ── An entry (§7.5) ───────────────────────────────────────────────────────
  recordedBy: (who: string, via: 'whatsapp' | 'app' | 'other', time: string) =>
    via === 'other'
      ? `Recorded by ${who} · ${time}`
      : `Recorded by ${who} on ${via === 'whatsapp' ? 'WhatsApp' : 'the app'} · ${time}`,
  factCustomer: () => 'Customer',
  factPaid: () => 'Paid',
  factStillOwing: () => 'Still owing',
  factCategory: () => 'Category',
  factDate: () => 'Date',
  factBusiness: () => 'Business or personal',
  factDirection: () => 'Sale or expense',
  business: () => 'Business',
  personal: () => 'Personal',
  notSure: () => 'Not sure yet',
  noCustomer: () => 'No customer',
  noCategory: () => 'Not set',
  method: (m: Method) => ({ cash: 'Cash', transfer: 'Transfer', pos: 'POS', other: 'Not said' })[m],
  whatTheySent: (name: string | null) => (name ? `What ${name} sent` : 'What you sent'),
  photo: () => 'Photo',
  voiceNote: () => 'Voice note',
  noTranscript: () => 'No transcript',
  openPhoto: () => 'Open photo',
  play: () => 'Play',
  pause: () => 'Pause',
  history: () => 'History',
  versionFirst: (who: string, time: string) => `Recorded by ${who} · ${time}`,
  versionChanged: (who: string, what: string, time: string) => `${who} changed ${what} · ${time}`,
  versionRemoved: (who: string, time: string) => `Removed by ${who} · ${time}`,
  versionConfirmed: (who: string, time: string) => `${who} confirmed it · ${time}`,
  /** The facts a version changed, in words ("the amount and the date"). */
  changedFacts: (fields: readonly string[]) => {
    // A flip brings its category and what's owed with it: it's said as itself.
    if (fields.includes('direction')) return 'sale or expense';
    const words: Record<string, string> = {
      amountMinor: 'the amount',
      owed: 'what is still owed',
      date: 'the date',
      counterpartyId: 'the customer',
      categoryId: 'the category',
      businessOrPersonal: 'business or personal',
    };
    const said = [...new Set(fields.map((f) => words[f]).filter((w): w is string => Boolean(w)))];
    if (said.length === 0) return 'the entry';
    return said.length === 1 ? said[0]! : `${said.slice(0, -1).join(', ')} and ${said[said.length - 1]}`;
  },
  fixThis: () => 'Fix this',
  tapWhatsWrong: () => "Tap anything that's wrong.",
  looksRight: () => 'Looks right',
  removeSale: () => 'Remove sale',
  removeExpense: () => 'Remove expense',
  removedBanner: (who: string, time: string) => `Removed by ${who} · ${time}`,
  entryGone: () => "This entry isn't in these books.",

  // ── Fix this, one fact at a time (§7.6) ───────────────────────────────────
  askOwed: (name: string | null) => (name ? `How much does ${name} still owe?` : 'How much is still owed?'),
  nothing: () => 'Nothing',
  allOf: (minor: number) => `All ${N(minor)}`,
  askAmount: () => 'How much was it?',
  amountLabel: () => 'Amount',
  stillOwingLabel: () => 'Still owing',
  askDate: () => 'When was it?',
  anotherDay: () => 'Another day',
  dateLabel: () => 'Date',
  askCustomer: () => 'Who was it?',
  someoneNew: () => 'Someone new',
  theirName: () => 'Their name',
  askCategory: () => 'What was it for?',
  askBusiness: () => 'Was this for the business?',
  askDirection: () => 'Was this a sale or an expense?',
  owedNowPaid: (minor: number) => `${N(minor)} was still owed on this sale. As an expense it all counts as paid.`,
  save: () => 'Save',
  saved: () => 'Saved',
  amountUnclear: () => 'Type the amount in naira, like 6000 or 6k.',
  /** A fix, Looks right or Remove with no connection: nothing is queued (only captures are). */
  offlineNoChange: () => "No data right now, so nothing was changed. Try again when you're back online.",
  fixRefused: (reason: string | undefined) => (reason && FIX_REFUSED[reason]) || OUR_ERROR,

  // ── Remove (§7.7) ─────────────────────────────────────────────────────────
  removeTitle: (direction: 'in' | 'out') => (direction === 'in' ? 'Remove this sale?' : 'Remove this expense?'),
  removeWhat: (p: { direction: 'in' | 'out'; amountMinor: number; customer: string | null; owedMinor: number }) => {
    const who = p.customer ? `${p.direction === 'in' ? ' from' : ' to'} ${p.customer}` : '';
    const base = `${N(p.amountMinor)}${who} comes out of your totals`;
    return p.owedMinor > 0 && p.customer ? `${base}, and the ${N(p.owedMinor)} ${p.customer} owes comes off the tab.` : `${base}.`;
  },
  removeKeeps: () => 'The history keeps it, with your name.',
  keepIt: () => 'Keep it',
  removeRefused: (reason: string | undefined) => (reason && REMOVE_REFUSED[reason]) || OUR_ERROR,

  // ── Capture (§7.3) ────────────────────────────────────────────────────────
  newRecord: () => 'New record',
  captureHelp: () => 'Type it the way you would on WhatsApp.',
  captureLabel: () => 'What happened?',
  captureExample: () => 'Sold 2 bags of rice to Chinedu, 96k. Paid 90k.',
  snap: () => 'Snap',
  sayIt: () => 'Say it',
  send: () => 'Send',
  recordAnother: () => 'Record another',
  done: () => 'Done',
  holdToRecord: () => 'Hold to record',
  recording: (seconds: number) => `Recording ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`,
  releaseToSend: () => 'Let go to send. Slide away to cancel.',
  voiceTooLong: () => "That's 3 minutes. Sending it now.",
  micBlocked: () => 'Allow the microphone to send voice notes.',
  savedOffline: () => "Saved on your phone. It'll send when you're back online.",
  couldNotSend: (reason: string | undefined) =>
    `This one couldn't be sent: ${(reason && CAPTURE_FAILED[reason]) || 'something went wrong'}.`,
  tryAgain: () => 'Try again',
  delete: () => 'Delete',
  sentWhileOffline: () => 'Sent while you were offline',
  waitingToSend: (n: number) => (n === 1 ? '1 waiting to send' : `${n} waiting to send`),
  sending: () => 'Sending…',
  justNow: () => 'Just now',
  viaApp: () => 'App',
  offlineTitle: () => 'No data right now.',
  offlineText: () => "Records save on your phone and send when you're back.",
  offlineReading: (time: string) => `Offline · showing what you saw at ${time}`,

  // ── Errors and access (§6.2, §9) ──────────────────────────────────────────
  noLongerOn: (label: string) => `You're no longer on ${possessive(label)} books.`,
  booksPaused: () => 'These books are paused. Message us on WhatsApp.',
  logInAgain: () => 'Please log in again.',
  ourError: () => OUR_ERROR,
  roleCant: () => 'Ask the owner to do this.',
  noBooksYet: () => "You don't keep books with Zibanu yet. Message us on WhatsApp to start.",

  // ── Upgrade (§7.10) ───────────────────────────────────────────────────────
  upgradeTitle: () => 'The app comes with Business.',
  upgradeLead: () => 'Your books keep working on WhatsApp, as always.',
  messageUs: () => 'Message us',

  // ── Customers and More, until 3B and 3C (§6.1) ────────────────────────────
  whoOwesYou: () => 'Who owes you',
  nobodyOwes: () => 'Nobody owes you right now.',
  owingSince: (date: string) => `Since ${date}`,
  moreTitle: () => 'More',
  theme: () => 'Theme',
  themeAuto: () => 'Match my phone',
  themeLight: () => 'Light',
  themeDark: () => 'Dark',
  themeSunlight: () => 'Sunlight',
  help: () => 'Help',
  helpLead: () => 'Questions? Message us on WhatsApp.',
  logOut: () => 'Log out',

  // ── Alerts (§7.8) ─────────────────────────────────────────────────────────
  alertsTitle: () => 'Alerts',
  alertSent: () => 'Sent on WhatsApp',
  alertWaiting: () => 'Waiting for your next message',
  alertNotSent: () => 'Not sent',
  replyInWhatsApp: () => 'Reply in WhatsApp to answer this.',
  noAlerts: () => 'Nothing yet. News about your books shows up here.',

  // ── Installing and updating (§6.5) ────────────────────────────────────────
  install: () => 'Install',
  installLead: () => 'Put Zibanu on your home screen.',
  iosInstallHint: () => 'Tap Share, then Add to Home Screen.',
  updateReady: () => 'Update ready',
  reload: () => 'Reload',
  dismiss: () => 'Dismiss',
} as const;
